import { useState, useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "@/lib/firebase";
import {
  isEmailLink,
  getStoredLinkEmail,
  completeEmailLinkSignIn,
} from "@/features/auth/lib/email-link";
import { exchangeFirebaseToken } from "@/features/auth/lib/exchange";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { getDefaultRoute } from "@/features/auth/lib/page-routes";
import { SetPasswordForm } from "@/features/auth/components/SetPasswordForm";
import { ThemeToggle, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Label, Button } from "@nexus/ui";

export const Route = createFileRoute("/auth/link")({
  component: EmailLinkPage,
});

function EmailLinkPage() {
  const { t } = useTranslation();
  useDocumentTitle(t("page.title.magicLink"));
  const navigate = useNavigate();
  const lastVisitedRoute = useAuth((s) => s.lastVisitedRoute);

  const [step, setStep] = useState<"detecting" | "ask-email" | "set-password" | "exchanging" | "error">("detecting");
  const [email, setEmail] = useState(getStoredLinkEmail() ?? "");
  const [error, setError] = useState<string | null>(null);

  // On mount: check if URL is a valid email link then auto-complete sign-in
  useEffect(() => {
    const url = window.location.href;
    if (!isEmailLink(url)) {
      setStep("error");
      setError("Invalid or expired sign-in link.");
      return;
    }

    const storedEmail = getStoredLinkEmail();
    if (storedEmail) {
      void handleSignIn(storedEmail, url);
    } else {
      // Different device — ask for email
      setStep("ask-email");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSignIn(signInEmail: string, url: string) {
    try {
      setStep("detecting");
      const credential = await completeEmailLinkSignIn(signInEmail, url);

      // Check if user has a password provider — if not, prompt to set one
      const hasPassword = credential.user.providerData.some(
        (p) => p.providerId === "password",
      );

      if (!hasPassword) {
        setStep("set-password");
        return;
      }

      // Already has password — exchange for Nexus JWT
      await exchangeAndRedirect(credential);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign-in failed");
      setStep("error");
    }
  }

  async function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email) return;
    await handleSignIn(email, window.location.href);
  }

  async function handlePasswordSet(password: string) {
    // Re-authenticate with the new password to get a non-revoked token
    await signInWithEmailAndPassword(auth, email, password);
    await exchangeAndRedirect();
  }

  async function exchangeAndRedirect(credential?: import("firebase/auth").UserCredential) {
    setStep("exchanging");
    try {
      const user = credential?.user ?? (await import("firebase/auth")).getAuth().currentUser;
      if (!user) throw new Error("Not authenticated");
      const firebaseToken = await user.getIdToken(true);
      const { tenants } = await exchangeFirebaseToken(firebaseToken);
      const defaultRoute = getDefaultRoute(tenants, lastVisitedRoute);
      void navigate({ to: defaultRoute ?? "/login" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Token exchange failed");
      setStep("error");
    }
  }

  // Set password step
  if (step === "set-password") {
    return <SetPasswordForm onComplete={handlePasswordSet} />;
  }

  // Ask for email (opened on different device)
  if (step === "ask-email") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--background)] p-4">
        <div className="fixed right-4 top-4">
          <ThemeToggle />
        </div>
        <Card className="w-full max-w-sm">
          <CardHeader className="space-y-1 text-center">
            <CardTitle className="text-xl">{t("auth.title")}</CardTitle>
            <CardDescription>{t("auth.enterEmail")}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t("auth.email")}</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                />
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button type="submit" className="w-full">
                {t("auth.signIn")}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (step === "error") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--background)] p-4">
        <div className="fixed right-4 top-4">
          <ThemeToggle />
        </div>
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <CardTitle className="text-xl text-destructive">
              {t("errors.pageLoadFailed")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-center">
            <p className="text-sm text-[var(--muted-foreground)]">{error}</p>
            <Button variant="outline" onClick={() => void navigate({ to: "/login" })}>
              {t("errors.backToLogin")}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Detecting / exchanging — spinner
  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--background)] p-4">
      <div className="fixed right-4 top-4">
        <ThemeToggle />
      </div>
      <Card className="w-full max-w-sm">
        <CardContent className="flex flex-col items-center gap-4 py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-[var(--muted)] border-t-[var(--primary)]" />
          <p className="text-sm text-[var(--muted-foreground)]">
            {step === "exchanging" ? t("auth.signingIn") : t("common.loading")}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
